import type {PointerEvent, ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {SelectedFilter, StockButtonFilters, StockGetterParams, StockPrimaryCTA, StockResponse} from '@Panels/stock-panel/stock-panel.types';
import type {SourceTier} from '@Panels/stock-panel/stock-panel.types';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {StockGridIDs, STOCK_GRID_ITEM_DIMENSION} from '@Panels/stock-panel/stock-panel.types';
import {getUniqueString} from '@Utils/string.util';
import type {LazyLoadingResponse, StockBackendData, LazyLoadingOffsets} from '@Libraries/pmw-stock-media-library';
import {getStockLibraryAudioCategories, StockSources} from '@Libraries/pmw-stock-media-library';
import {ingestStockScoreDebug, isStockScoreDebugActive} from '@Panels/stock-panel/stock-score-debug';
import {type GridItemStorage, type GridStockMediaItemBackendData} from '@Components/base-grid/components/grid-item';
import {
  aiAnimatedStickerDataToAiAnimatedStickerItem,
  aiIconDataToAiIconItem,
  aiStickerDataToAiStickerItem,
  animatedStickerDataToAnimatedStickerItem,
  audioDataToAudioItem,
  clipartDataToClipartItem,
  extractedGettyStickerDataToStickerItem,
  iconDataToIconItem,
  imageDataToImageItem,
  shapeDataToShapeItem,
  stillStickerDataToStickerItem,
  videoDataToVideoItem,
} from '@Panels/stock-panel/stock-panel.library';
import type {GridStockImageItemBackEndData} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridStockVideoItemBackEndData} from '@Components/base-grid/components/grid-stock-video-item';
import type {GridClipartItemBackEndData} from '@Components/base-grid/components/grid-clipart-item';
import StockPanelError from '@Panels/stock-panel/components/stock-panel-error';
import {useStoryblocksAvailability} from '@Panels/stock-panel/components/stock-panel-grid/stock-panel-grid.hooks';
import {clearSelectedItems, getNoOfGridItems, reloadGrid, toggleGridItemSelection} from '@Components/base-grid';
import StockPanelNoResults from '@Panels/stock-panel/components/stock-panel-no-results';
import {StockAiButton} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button';
import type {AiPanelKey} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button.types';
import type {PMWStockBackendAiAnimatedSticker, PMWStockBackendAnimatedSticker, PMWStockBackendShape} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {Dimensions} from '@floating-ui/react';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {noop} from '@Utils/general.util';
import type {GridAiIconItemBackEndData, GridIconItemBackEndData} from '@Components/base-grid/components/grid-icon-item/grid-icon-item.types';
import type {GridAiStickerItemBackEndData, GridStillStickerItemBackEndData} from '@Components/base-grid/components/grid-still-sticker-item/grid-still-sticker-item.types';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {CarouselClickBehaviour} from '@Components/carousel/carousel.types';

export interface StockPanelGridProps {
  maxSelectionNumber?: number;
  gridID: string;
  sources: SourceTier[];
  className?: string;
  selectionGroupID?: string;
  searchTerm?: string;
  onSingleItemClick?: StockPrimaryCTA;
  clickBehaviour?: CarouselClickBehaviour;
  enableDragToCanvas?: boolean;
  gridItemDimensions?: Dimensions;
  isManageappPage?: boolean;
  selectedTab?: StockPanelTabs;
  selectedFilters?: SelectedFilter[];
  scrollingContainer?: HTMLDivElement | null;
  onItemPointerDown?: (item: GridItemStorage, e: PointerEvent<HTMLElement>) => void;

  generateImageCallback(): void;

  onInitialBatchLoaded?(): void;

  onNoResultsChange?(noResults: boolean): void;

  onAiCtaClicked?(key: AiPanelKey): void;
}

export default function StockPanelGrid({...props}: StockPanelGridProps): ReactElement<any> {
  const selectionGroupID = useRef(props.selectionGroupID ?? getUniqueString());
  const [noResults, setNoResults] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const storyblocks = useStoryblocksAvailability();
  const dispatch = useAppDispatch();
  const currentResults = useAppSelector((state) => {
    return getNoOfGridItems(state, props.gridID);
  });
  const stockGetterOptions = useAppSelector((state) => {
    return state.stockPanel.stockGetterOptions;
  });

  const gridRef = useRef<HTMLDivElement | null>(null);
  const audioCategories = useRef<Record<string, string>>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const seenIds = useRef<Set<string>>(new Set());

  const onGridItemClicked = (item: GridItemStorage): void => {
    if (props.clickBehaviour === CarouselClickBehaviour.SELECT) {
      dispatch(
        toggleGridItemSelection({
          gridItemId: item.id,
          gridSelectionGroupId: selectionGroupID.current,
        })
      );
      return;
    }
    if (props.onSingleItemClick) {
      void props.onSingleItemClick([item]);
    }
  };

  const isTieredSource = (sources: StockSources[] | SourceTier[]): sources is SourceTier[] => {
    return sources.length > 0 && typeof sources[0] === 'object' && ('internalStock' in sources[0] || 'externalStock' in sources[0]);
  };

  const getAdditionalSearchParams = (): string => {
    return stockGetterOptions.seasonalSearch ?? '';
  };

  const fetchStockMedia = async (tiers: SourceTier[], limit: number): Promise<StockResponse> => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    const queryParams = new URLSearchParams();
    queryParams.append('limit', `${limit}`);
    queryParams.append('searchTerm', `${props.searchTerm ?? ''} ${getAdditionalSearchParams()}`);
    queryParams.append('isManageappPage', props.isManageappPage ? '1' : '0');
    queryParams.append('options', JSON.stringify(stockGetterOptions));
    queryParams.append('tiers', JSON.stringify(tiers));
    queryParams.append('tab', props.selectedTab ?? '');
    queryParams.append('selectedFilters', JSON.stringify(props.selectedFilters ?? []));

    if (isStockScoreDebugActive()) {
      queryParams.append('debugScores', '1');
    }

    if (stockGetterOptions.audioFilters) {
      Object.keys(stockGetterOptions.audioFilters).forEach((id) => {
        if (stockGetterOptions.audioFilters) {
          stockGetterOptions.audioFilters[id as StockButtonFilters].forEach((idSubfilter) => {
            const name = audioCategories.current[idSubfilter];
            trackPosterBuilderGA4Events(GA4EventName.MediaModal_AudioFilters, {[GA4EventParamName.VALUE]: name});
          });
        }
      });
    }

    const url = `${window.PMW.util.site_url('stockmedia/getStockV2')}?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Cache-Control': 'max-age=600',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = (await response.json()) as AjaxSuccessResponse;
    const stockResponse = data.data as StockResponse;
    ingestStockScoreDebug(stockResponse.debug);
    return stockResponse;
  };

  const getItems = async (getterParams: StockGetterParams): Promise<LazyLoadingResponse> => {
    if (props.sources.length === 0) {
      setNoResults(true);
      return {response: [], offsetsData: {}};
    }
    if (!isTieredSource(props.sources)) {
      return {response: [], offsetsData: {}};
    }

    const tiers = props.sources;
    const offsets = getterParams.offsets;

    const tiersToRequest = tiers.map((tier, tierIndex) => {
      const newTier = {...tier};
      if (newTier.internalStock) {
        newTier.internalStock = {...newTier.internalStock};
        const internalSourcesKey = [...newTier.internalStock.stockSources].sort().join('_');
        const key = `tier_${tierIndex}_internal_${internalSourcesKey}`;
        newTier.internalStock.offset = offsets[key] ?? 0;
      }
      if (newTier.externalStock) {
        newTier.externalStock = newTier.externalStock.map((ext, extIndex) => {
          const newExt = {...ext};
          const key = `tier_${tierIndex}_external_${extIndex}_${newExt.stockSource}`;
          newExt.offset = offsets[key] ?? 0;
          return newExt;
        });
      }
      return newTier;
    });

    let stock: StockBackendData[];
    let tierResults: StockResponse['tierResults'];

    try {
      const responseFromBackend = await fetchStockMedia(tiersToRequest, getterParams.limit);
      stock = responseFromBackend.stock;
      tierResults = responseFromBackend.tierResults;
      storyblocks.sync(responseFromBackend.storyblocksAvailable);
      setErrorState(false);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return {response: [], offsetsData: {}};
      }
      console.error(e);
      setErrorState(true);
      return {response: [], offsetsData: {}};
    }

    const nextOffsets: LazyLoadingOffsets = {};
    tierResults.forEach((result, tierIndex) => {
      const tier = tiers[tierIndex];
      if (result.internal !== undefined && tier.internalStock) {
        const internalSourcesKey = [...tier.internalStock.stockSources].sort().join('_');
        const key = `tier_${tierIndex}_internal_${internalSourcesKey}`;
        nextOffsets[key] = (nextOffsets[key] ?? 0) + result.internal;
      }
      if (result.external && tier.externalStock) {
        result.external.forEach((usage, extIndex) => {
          if (!tier.externalStock) {
            return;
          }
          const ext = tier.externalStock?.[extIndex];
          const key = `tier_${tierIndex}_external_${extIndex}_${ext.stockSource}`;
          nextOffsets[key] = (nextOffsets[key] ?? 0) + usage;
        });
      }
    });

    const response: GridItemStorage[] = [];

    if (stock.length <= 0 && currentResults <= 0) {
      setNoResults(true);
      return {response: [], offsetsData: {}};
    }

    setNoResults(false);

    stock.forEach((stockItemData): void => {
      mapBackendDataToGridItem(stockItemData, response);
    });

    return {response, offsetsData: nextOffsets};
  };

  const mapBackendDataToGridItem = (stockItemData: StockBackendData, response: GridItemStorage[]): void => {
    switch (stockItemData.type as StockSources) {
      case StockSources.PMW_ANIMATED_STICKER:
        response.push(animatedStickerDataToAnimatedStickerItem(stockItemData as PMWStockBackendAnimatedSticker));
        break;
      case StockSources.PMW_AI_ANIMATED_STICKER:
        response.push(aiAnimatedStickerDataToAiAnimatedStickerItem(stockItemData as unknown as PMWStockBackendAiAnimatedSticker));
        break;
      case StockSources.PMW_SHAPE:
        response.push(shapeDataToShapeItem(stockItemData as PMWStockBackendShape));
        break;
      case StockSources.GETTY_IMAGE:
      case StockSources.STORYBLOCK_IMAGE:
      case StockSources.PIXABY_IMAGE:
      case StockSources.PMW_STOCK_IMAGE:
        response.push(imageDataToImageItem(stockItemData as GridStockImageItemBackEndData, stockItemData.type as StockSources));
        break;
      case StockSources.GETTY_VIDEO:
      case StockSources.STORYBLOCK_VIDEO:
      case StockSources.PIXABY_VIDEO: {
        const videoGridItem = videoDataToVideoItem(stockItemData as GridStockVideoItemBackEndData, stockItemData.type as StockSources);
        if (videoGridItem) {
          response.push(videoGridItem);
        }
        break;
      }
      case StockSources.STORYBLOCK_ILLUSTRATION:
      case StockSources.PIXABY_ILLUSTRATION:
      case StockSources.GETTY_ILLUSTRATION:
        response.push(clipartDataToClipartItem(stockItemData as unknown as GridClipartItemBackEndData, stockItemData.type as StockSources));
        break;
      case StockSources.STORYBLOCK_AUDIO:
        response.push(audioDataToAudioItem(stockItemData));
        break;
      case StockSources.PMW_AI_ICON:
        response.push(aiIconDataToAiIconItem(stockItemData as unknown as GridAiIconItemBackEndData));
        break;
      case StockSources.PMW_ICON:
        response.push(iconDataToIconItem(stockItemData as unknown as GridIconItemBackEndData));
        break;
      case StockSources.PMW_EXTRACTED_GETTY_STICKER:
        response.push(extractedGettyStickerDataToStickerItem(stockItemData as unknown as GridStockMediaItemBackendData));
        break;
      case StockSources.PMW_STILL_STICKER:
        response.push(stillStickerDataToStickerItem(stockItemData as unknown as GridStillStickerItemBackEndData));
        break;
      case StockSources.PMW_AI_STICKER:
        response.push(aiStickerDataToAiStickerItem(stockItemData as unknown as GridAiStickerItemBackEndData));
        break;
      default:
        throw new Error(`Unhandled stock source: ${stockItemData.type ?? ''}`);
    }
  };

  const getGrid = (): ReactElement<any> => {
    const gridProps = {
      getItems: getItems,
      customOnGridItemClicked: onGridItemClicked,
      className: ` ${props.className ?? ''}`,
      gridSelectionGroupId: selectionGroupID.current,
      maxSelectionNumber: props.maxSelectionNumber,
      clearGridOnUnmount: true,
      gridId: props.gridID,
      customDimensions: props.gridItemDimensions,
      isItemDraggable: props.enableDragToCanvas,
      onItemPointerDown: props.onItemPointerDown,
      transformResponse: (items: GridItemStorage[]): GridItemStorage[] => {
        return items.filter((item) => {
          if (seenIds.current.has(item.id)) {
            return false;
          }
          seenIds.current.add(item.id);
          return true;
        });
      },
    };

    return (
      <LazyMasonryGrid
        key={props.gridID}
        {...gridProps}
        gridRef={(ref) => {
          gridRef.current = ref;
        }}
        initialItemsBatchLoaded={props.onInitialBatchLoaded}
        makeContentScrollable={false}
        scrollingContainer={props.scrollingContainer}
        doCheckForNextBatchImmediately={false}
        defaultHeight={STOCK_GRID_ITEM_DIMENSION}
        data-testid={PosterEditorTestId.STOCK_PANE_LAZY_GRID}
      />
    );
  };

  const getAiCtaForNoResults = (): ReactElement | undefined => {
    if (!props.onAiCtaClicked) {
      return undefined;
    }
    return (
      <StockAiButton
        isGridView={false}
        selectedTab={props.selectedTab ?? StockPanelTabs.PHOTOS}
        tabsToShow={[props.selectedTab ?? StockPanelTabs.PHOTOS]}
        widthMode="content"
        onAiImages={(): void => props.onAiCtaClicked?.('ai-images')}
        onAiStickers={(): void => props.onAiCtaClicked?.('ai-stickers')}
        onAiVideos={(): void => props.onAiCtaClicked?.('ai-videos')}
        onAiVoice={(): void => props.onAiCtaClicked?.('ai-voice')}
      />
    );
  };

  const getNoResultsPanel = (): ReactElement<any> => {
    if (props.selectedTab === StockPanelTabs.AUDIOS && storyblocks.isUnavailable) {
      return <StockPanelError className="_full-height _full-width" text={window.i18next.t('pmwjs_stock_audio_clips_unavailable_temporarily')} />;
    }

    return (
      <div className="spacing-m-t-6 _full-width _full-height">
        <StockPanelNoResults searchTerm={props.searchTerm ?? ''} aiCta={getAiCtaForNoResults()} />
      </div>
    );
  };

  useEffect(() => {
    setNoResults(false);
    seenIds.current = new Set();
    if (props.gridID !== StockGridIDs.TEXT_TO_IMAGE.valueOf()) {
      dispatch(
        reloadGrid({
          gridId: props.gridID,
          gridSelectionGroupId: selectionGroupID.current,
        })
      );

      dispatch(
        clearSelectedItems({
          gridSelectionGroupId: selectionGroupID.current,
        })
      );
    }

    if (gridRef.current) {
      gridRef.current.scrollTo({top: 0, behavior: 'smooth'});
    }

    return noop;
  }, [props.searchTerm, JSON.stringify(props.sources), stockGetterOptions, props.gridID]);

  const init = async (): Promise<void> => {
    const [, response] = await Promise.all([storyblocks.loadInitial(), getStockLibraryAudioCategories()]);
    audioCategories.current = response
      .flatMap((category) => category.subcategories)
      .reduce<Record<string, string>>((acc, subcategory) => {
        acc[subcategory.id.toString()] = subcategory.displayName;
        return acc;
      }, {});
  };

  useEffect(() => {
    void init();
    return noop;
  }, []);

  useEffect(() => {
    props.onNoResultsChange?.(noResults);
    return noop;
  }, [noResults]);

  if (errorState) {
    return (
      <div className="spacing-m-t-6 _full-width _full-height">
        <StockPanelError
          text={window.i18next.t('pmwjs_refresh_on_error')}
          onRefresh={(): void => {
            seenIds.current = new Set();
            dispatch(
              reloadGrid({
                gridId: props.gridID,
                gridSelectionGroupId: selectionGroupID.current,
              })
            );
            setErrorState(false);
          }}
        />
      </div>
    );
  }

  return noResults ? getNoResultsPanel() : getGrid();
}
